import { madSenseSelectionCompatible } from './madSenseCompatibility';
import type { ReportFilter } from './model';
import { meetsReportSelectionRestrictions } from './selectionRestrictions';
import { ReportDimension, ReportMetric } from './reportEnums';
import { contextualReportDimensions, isContextualReportDimension } from './reportRequest';

const contextualCompatibleDimensions = new Set<ReportDimension>([
  ReportDimension.Day,
  ReportDimension.Hour,
  ReportDimension.Month,
  ReportDimension.Year,
  ReportDimension.Network,
  ReportDimension.Publisher,
  ReportDimension.App,
  ReportDimension.PublisherDomain,
  ReportDimension.Placement,
  ReportDimension.BidderCode,
  ReportDimension.DemandIntegration,
  ReportDimension.DemandPartner,
  ReportDimension.DemandPartnerOwner,
  ...contextualReportDimensions,
]);

const contextualCompatibleMetrics = new Set<ReportMetric>([
  ReportMetric.Day,
  ReportMetric.Hour,
  ReportMetric.NetworkId,
  ReportMetric.BidderCode,
  ReportMetric.DemandIntegrationId,
  ReportMetric.DemandPartnerId,
  ReportMetric.DemandPartnerName,
  ReportMetric.DemandPartnerOwnerId,
  ReportMetric.DemandPartnerOwnerName,
  ReportMetric.ContentTitle,
  ReportMetric.ContentSeries,
  ReportMetric.ContentGenre,
  ReportMetric.ContentCat,
  ReportMetric.ContentRating,
  ReportMetric.ContentChannelName,
  ReportMetric.ContentNetworkName,
  ReportMetric.ContentLanguage,
  ReportMetric.ContentLivestream,
  ReportMetric.AdRequests,
  ReportMetric.DpAdRequests,
  ReportMetric.WonBids,
  ReportMetric.AdImpressions,
  ReportMetric.Rpm,
  ReportMetric.Revenue,
  ReportMetric.Profit,
  ReportMetric.GrossRevenue,
  ReportMetric.PubRevenue,
]);

const regionCountryMetrics = new Set<ReportMetric>([
  ReportMetric.Country,
  ReportMetric.Region,
  ReportMetric.AdRequests,
  ReportMetric.AdImpressions,
  ReportMetric.Revenue,
  ReportMetric.PublisherName,
  ReportMetric.Domain,
  ReportMetric.AppBundleId,
  ReportMetric.Placement,
  ReportMetric.DemandIntegration,
  ReportMetric.DemandIntegrationId,
  ReportMetric.DemandPartnerId,
  ReportMetric.DemandPartnerName,
  ReportMetric.DemandPartnerOwnerId,
  ReportMetric.DemandPartnerOwnerName,
]);

const regionCountryDimensions = new Set<ReportDimension>([
  ReportDimension.Country,
  ReportDimension.Region,
  ReportDimension.Publisher,
  ReportDimension.PublisherDomain,
  ReportDimension.App,
  ReportDimension.Placement,
  ReportDimension.DemandIntegration,
  ReportDimension.DemandPartner,
  ReportDimension.DemandPartnerOwner,
]);

const errorMetrics = new Set<ReportMetric>([
  ReportMetric.ErrorCode,
  ReportMetric.ErrorMessage,
  ReportMetric.Errors,
  ReportMetric.PublisherName,
  ReportMetric.Domain,
  ReportMetric.AppBundleId,
  ReportMetric.BidderCode,
  ReportMetric.DemandPartnerId,
  ReportMetric.DemandPartnerName,
  ReportMetric.DemandPartnerOwnerId,
  ReportMetric.DemandPartnerOwnerName,
  ReportMetric.VideoName,
  ReportMetric.Xp,
  ReportMetric.Xp1,
  ReportMetric.Xp2,
  ReportMetric.Xp3,
  ReportMetric.NetworkName,
  ReportMetric.SchainAsi,
  ReportMetric.Placement,
  ReportMetric.DemandIntegration,
  ReportMetric.LineItem,
  ReportMetric.Day,
  ReportMetric.Hour,
  ReportMetric.Order,
  ReportMetric.Country,
  ReportMetric.Region,
]);

const dvMetrics = new Set<ReportMetric>([
  ReportMetric.DvSegment,
  ReportMetric.DpAdRequests,
  ReportMetric.FilledImpressions,
  ReportMetric.WonBids,
  ReportMetric.PublisherSecondaryId,
  ReportMetric.PublisherName,
  ReportMetric.PublisherId,
  ReportMetric.AppBundleId,
  ReportMetric.NetworkName,
  ReportMetric.NetworkId,
  ReportMetric.BidderCode,
  ReportMetric.DemandPartnerId,
  ReportMetric.DemandPartnerName,
  ReportMetric.DemandPartnerOwnerId,
  ReportMetric.DemandPartnerOwnerName,
  ReportMetric.Placement,
  ReportMetric.PlacementId,
  ReportMetric.Day,
  ReportMetric.Hour,
  ReportMetric.Year,
  ReportMetric.Month,
]);

const dvDimensions = new Set<ReportDimension>([
  ReportDimension.DvSegment,
  ReportDimension.Publisher,
  ReportDimension.Network,
  ReportDimension.App,
  ReportDimension.Placement,
  ReportDimension.BidderCode,
  ReportDimension.DemandPartner,
  ReportDimension.DemandPartnerOwner,
]);

const bidFloorDimensions = new Set<ReportDimension>([
  ReportDimension.Publisher,
  ReportDimension.App,
  ReportDimension.BidfloorReq,
  ReportDimension.Placement,
  ReportDimension.Network,
  ReportDimension.PublisherDomain,
  ReportDimension.Day,
  ReportDimension.Application,
]);

const ivtMetrics = new Set<ReportMetric>([
  ReportMetric.IvtProvider,
  ReportMetric.IvtType,
  ReportMetric.IvtTotalEvents,
  ReportMetric.IvtInvalidTraffic,
  ReportMetric.IvtInvalidTrafficPct,
  ReportMetric.IvtSofisticated,
  ReportMetric.IvtSofisticatedPct,
  ReportMetric.IvtGeneral,
  ReportMetric.IvtGeneralPct,
  ReportMetric.IvtValidTraffic,
  ReportMetric.IvtValidTrafficPct,
]);

export function isReportMetricCompatible(
  candidate: ReportMetric,
  selectedMetrics: readonly ReportMetric[],
  selectedDimensions: readonly ReportDimension[] = [],
  filters: Partial<ReportFilter> = {},
) {
  const prospective = [...selectedMetrics, candidate];
  const madSense = madSenseSelectionCompatible(selectedDimensions, prospective, filters);
  if (madSense !== null) return madSense;
  if (!meetsReportSelectionRestrictions(selectedDimensions, prospective, filters)) return false;
  if (prospective.some(isBidFloorMetric)) {
    return selectedDimensions.every((dimension) => bidFloorDimensions.has(dimension));
  }
  if (selectedDimensions.some(isContextualReportDimension)) {
    return prospective.every((metric) => contextualCompatibleMetrics.has(metric));
  }
  if (
    prospective.some((metric) => metric === ReportMetric.Country || metric === ReportMetric.Region)
  )
    return prospective.every((metric) => regionCountryMetrics.has(metric));
  if (
    prospective.some(
      (metric) =>
        metric === ReportMetric.ErrorCode ||
        metric === ReportMetric.ErrorMessage ||
        metric === ReportMetric.Errors,
    )
  )
    return prospective.every((metric) => errorMetrics.has(metric));
  if (prospective.includes(ReportMetric.DvSegment))
    return prospective.every((metric) => dvMetrics.has(metric));
  if (prospective.some((metric) => ivtMetrics.has(metric))) {
    return (
      !prospective.includes(ReportMetric.Country) &&
      !prospective.includes(ReportMetric.Region) &&
      !prospective.includes(ReportMetric.DvSegment)
    );
  }
  return true;
}

export function isReportDimensionCompatible(
  candidate: ReportDimension,
  selectedDimensions: readonly ReportDimension[],
  selectedMetrics: readonly ReportMetric[] = [],
  filters: Partial<ReportFilter> = {},
) {
  const prospective = [...selectedDimensions, candidate];
  const madSense = madSenseSelectionCompatible(prospective, selectedMetrics, filters);
  if (madSense !== null) return madSense;
  if (!meetsReportSelectionRestrictions(prospective, selectedMetrics, filters)) return false;
  if (selectedMetrics.some(isBidFloorMetric)) {
    return prospective.every((dimension) => bidFloorDimensions.has(dimension));
  }
  if (prospective.some(isContextualReportDimension)) {
    return (
      prospective.every((dimension) => contextualCompatibleDimensions.has(dimension)) &&
      selectedMetrics.every((metric) => contextualCompatibleMetrics.has(metric))
    );
  }
  if (
    prospective.some(
      (dimension) => dimension === ReportDimension.Country || dimension === ReportDimension.Region,
    )
  )
    return prospective.every((dimension) => regionCountryDimensions.has(dimension));
  if (prospective.includes(ReportDimension.BidfloorReq))
    return prospective.every((dimension) => bidFloorDimensions.has(dimension));
  if (prospective.includes(ReportDimension.DvSegment))
    return prospective.every((dimension) => dvDimensions.has(dimension));
  if (prospective.includes(ReportDimension.ErrorCode))
    return (
      !prospective.includes(ReportDimension.Month) && !prospective.includes(ReportDimension.Year)
    );
  if (
    prospective.some(
      (dimension) =>
        dimension === ReportDimension.IvtType || dimension === ReportDimension.IvtProvider,
    )
  ) {
    return (
      !prospective.includes(ReportDimension.Country) &&
      !prospective.includes(ReportDimension.Region) &&
      !prospective.includes(ReportDimension.DvSegment)
    );
  }
  return true;
}

function isBidFloorMetric(metric: ReportMetric) {
  return metric === ReportMetric.OutgoingFloor || metric === ReportMetric.FloorEcpm;
}

export function isReportSelectionCompatible(
  dimensions: readonly ReportDimension[],
  metrics: readonly ReportMetric[],
  filters: Partial<ReportFilter> = {},
) {
  return (
    meetsReportSelectionRestrictions(dimensions, metrics, filters) &&
    dimensions.every((dimension) =>
      isReportDimensionCompatible(dimension, dimensions, metrics, filters),
    ) &&
    metrics.every((metric) => isReportMetricCompatible(metric, metrics, dimensions, filters))
  );
}

export interface ReportSelectionConflicts {
  dimensions: ReportDimension[];
  metrics: ReportMetric[];
  filters: boolean;
}

/** Selected items whose removal alone would make `candidate` selectable (for "why disabled"). */
export function reportDimensionConflicts(
  candidate: ReportDimension,
  selectedDimensions: readonly ReportDimension[],
  selectedMetrics: readonly ReportMetric[],
  filters: Partial<ReportFilter> = {},
): ReportSelectionConflicts {
  return {
    dimensions: selectedDimensions.filter((dimension) =>
      isReportDimensionCompatible(
        candidate,
        selectedDimensions.filter((item) => item !== dimension),
        selectedMetrics,
        filters,
      ),
    ),
    metrics: selectedMetrics.filter((metric) =>
      isReportDimensionCompatible(
        candidate,
        selectedDimensions,
        selectedMetrics.filter((item) => item !== metric),
        filters,
      ),
    ),
    filters: isReportDimensionCompatible(candidate, selectedDimensions, selectedMetrics),
  };
}

export function reportMetricConflicts(
  candidate: ReportMetric,
  selectedMetrics: readonly ReportMetric[],
  selectedDimensions: readonly ReportDimension[],
  filters: Partial<ReportFilter> = {},
): ReportSelectionConflicts {
  return {
    dimensions: selectedDimensions.filter((dimension) =>
      isReportMetricCompatible(
        candidate,
        selectedMetrics,
        selectedDimensions.filter((item) => item !== dimension),
        filters,
      ),
    ),
    metrics: selectedMetrics.filter((metric) =>
      isReportMetricCompatible(
        candidate,
        selectedMetrics.filter((item) => item !== metric),
        selectedDimensions,
        filters,
      ),
    ),
    filters: isReportMetricCompatible(candidate, selectedMetrics, selectedDimensions),
  };
}
